/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    IHTTPStatusMatch,
    IHTTPStatusRange,
    MatchOperation,
} from 'generated-types';

import { HTTPStatusMatch } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { RepeatedMessageItem }
    from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { TEnumValueLabelsHash } from 'ajs/modules/core/services/schema-service/schema.types';

type THTTPStatusMatchPartial = Omit<IHTTPStatusMatch, 'ranges'>;

interface IHTTPStatusMatchConfig extends THTTPStatusMatchPartial {
    ranges?: RepeatedMessageItem<MessageItem<IHTTPStatusRange>>;
}

/**
 * @description HTTPStatusMatch ConfigItem class.
 * @author Abhinesh Gour
 */
export class HTTPStatusMatchConfigItem extends MessageItem<IHTTPStatusMatchConfig> {
    public static ajsDependencies = ['schemaService'];

    /**
     * SchemaService instance to get data model exported from protobuf.
     */
    private readonly schemaService: SchemaService;

    /**
     * Hash of match operation values to their labels.
     */
    private readonly matchOperationLabelsHash: TEnumValueLabelsHash;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPStatusMatch,
            ...args,
        };

        super(extendedArgs);

        this.schemaService = this.getAjsDependency_('schemaService');
        this.matchOperationLabelsHash = this.schemaService.getEnumValueLabelsHash('MatchOperation');
    }

    /**
     * Method to merge status code and ranges into single array of string.
     */
    public getRangesAndCodesAsList(): string[] {
        let rangesAndCodes: string[] = [];

        const {
            ranges: { config: rangesConfig },
            status_codes: statusCodes,
        } = this.config;

        if (Array.isArray(statusCodes)) {
            const codes = statusCodes.map(code => `${code}`);

            rangesAndCodes = [...rangesAndCodes, ...codes];
        }

        if (Array.isArray(rangesConfig)) {
            rangesAndCodes =
                [...rangesAndCodes, ...rangesConfig.map(r => `${r.config.begin}-${r.config.end}`)];
        }

        return rangesAndCodes;
    }

    /**
     * setStatusCodesAndRanges takes string of status codes and ranges
     * and split array into statusCodes and StatusRanges.
     */
    public setStatusCodesAndRanges(codesAndRanges: string[]): void {
        const statusCodeAndRangeList = codesAndRanges;
        const statusCodes: number[] = [];
        const statusRanges: string[] = [];

        if (Array.isArray(statusCodeAndRangeList) && statusCodeAndRangeList.length) {
            statusCodeAndRangeList.forEach(item => {
                if (item?.trim()) {
                    if (item.includes('-')) {
                        statusRanges.push(item);
                    } else {
                        statusCodes.push(+item);
                    }
                }
            });
        }

        this.bindRangeAndStatusCodes(statusRanges, statusCodes);
    }

    /**
     * Method takes array of statusRanges and statusCodes update match config.
     */
    public bindRangeAndStatusCodes(statusRanges: string[], statusCodes: number[]): void {
        const ranges: IHTTPStatusRange[] = [];

        if (Array.isArray(statusRanges) && statusRanges.length) {
            statusRanges.forEach(range => {
                const subRange = range.split('-');

                if (subRange.length === 2) {
                    const begin = parseInt(subRange[0], 10);
                    const end = parseInt(subRange[1], 10);

                    if (!Number.isNaN(begin) && !Number.isNaN(end)) {
                        ranges.push({
                            begin,
                            end,
                        });
                    }
                }
            });
        }

        this.config.ranges.updateConfig(ranges);
        this.config.status_codes = statusCodes;
    }

    /**
     * Returns the label to display for this.config.match_criteria.
     */
    public get matchCriteriaLabel(): string {
        const { match_criteria: matchCriteria } = this.config;

        return this.matchOperationLabelsHash[matchCriteria];
    }

    /**
     * Returns a string representation of the config data.
     */
    public toString(): string {
        return `${this.matchCriteriaLabel} ${this.getRangesAndCodesAsList().join(', ')}`;
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IHTTPStatusMatchConfig> {
        const defaultValues = this.getAjsDependency_('defaultValues');
        const defaultConfig = defaultValues.getDefaultItemConfigByType(this.objectType) || {};

        return {
            ...defaultConfig,
            match_criteria: MatchOperation.IS_IN,
        };
    }
}
